import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { AuthShell } from "@/components/auth/AuthShell";
import { errorMessage } from "@/components/auth/issues";
import { Button, Checkbox, TextField } from "@/components/ui/FormControls";
import { api } from "@/lib/api";
import { useAuth } from "@/store/auth";

type Invitation = { email: string; existing: boolean };

export function AdminActivatePage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const nav = useNavigate();
  const load = useAuth((s) => s.load);
  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [organization, setOrganization] = useState("");
  const [phone, setPhone] = useState("");
  const [consentPrivacy, setConsentPrivacy] = useState(false);

  useEffect(() => {
    if (!token) { setError("초대 링크가 없습니다."); return; }
    void api<Invitation>(`/auth/admin-invite?token=${encodeURIComponent(token)}`)
      .then(setInvitation)
      .catch((err) => setError(errorMessage(err)));
  }, [token]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api("/auth/admin-invite/accept", {
        method: "POST",
        json: { token, password, ...(invitation?.existing ? {} : { name, organization, phone, consentPrivacy }) },
      });
      await load();
      nav("/admin", { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell title="관리자 계정 설정" sub="초대받은 이메일을 확인하고 새 비밀번호를 설정합니다." footer={<Link to="/login">로그인으로 돌아가기</Link>}>
      {invitation ? (
        <form onSubmit={submit} className="auth-form">
          <TextField label="관리자 이메일" type="email" value={invitation.email} readOnly />
          <TextField label="새 비밀번호" type="password" autoComplete="new-password" minLength={8} required value={password} onChange={(e) => setPassword(e.target.value)} hint="8자 이상. 이전 비밀번호가 기억나지 않아도 설정할 수 있습니다." />
          {!invitation.existing && (
            <>
              <TextField label="이름" required value={name} onChange={(e) => setName(e.target.value)} />
              <TextField label="회사 또는 기관명" required value={organization} onChange={(e) => setOrganization(e.target.value)} />
              <TextField label="연락처" type="tel" required minLength={7} value={phone} onChange={(e) => setPhone(e.target.value)} />
              <Checkbox label="[필수] 개인정보 수집 및 이용 동의" required checked={consentPrivacy} onChange={(e) => setConsentPrivacy(e.target.checked)} description={<>계정 운영을 위해 이메일, 이름, 회사, 연락처를 수집합니다. <Link to="/privacy" target="_blank" rel="noreferrer">개인정보처리방침 보기</Link></>} />
            </>
          )}
          {error && <p role="alert" className="note note-danger text-label">{error}</p>}
          <Button type="submit" variant="primary" fullWidth disabled={busy || !password || (!invitation.existing && (!name || !organization || phone.length < 7 || !consentPrivacy))}>{busy ? "활성화 중…" : "관리자 계정 활성화"}</Button>
        </form>
      ) : <p role={error ? "alert" : "status"} className={error ? "note note-danger" : "text-ink-muted"}>{error ?? "초대 링크를 확인하고 있습니다…"}</p>}
    </AuthShell>
  );
}
